/**
 * `phone` — inline input plus the country's flag
 * (`discovery/ui-reference/editors/phone/capture.json`: an in-cell editor 179 × 35, 14px/400,
 * `opensOnEnter` true; README: *"Inline input plus a country selector"*).
 *
 * The country selector itself is **not** captured — monday's is a searchable list with dial
 * codes we have no source for — so this ships the flag as a read-only affordance and keeps the
 * stored `countryShortName`, which is the conservative reading (q331).
 */
import { getColumnType, type PhoneSettings, type PhoneValue } from '@elizmonday/shared';
import { InlineTextEditor } from '../primitives';
import { defineColumnUi } from '../types';
import { sharedBuckets } from '../buckets';
import { PhoneSettingsDialog } from './SettingsDialog';

const shared = getColumnType('phone');

/** ISO 3166-1 alpha-2 → the regional-indicator pair that renders as a flag. */
export function flagOf(code: string | null | undefined): string {
  if (typeof code !== 'string' || code.length !== 2) return '';
  const base = 0x1f1e6 - 'A'.charCodeAt(0);
  return String.fromCodePoint(base + code.toUpperCase().charCodeAt(0), base + code.toUpperCase().charCodeAt(1));
}

export const phoneColumnUi = defineColumnUi<PhoneValue, PhoneSettings>({
  type: 'phone',
  icon: 'columnGeneric',
  gallery: { group: 'essentials', keywords: ['phone', 'number', 'mobile', 'contact'] },
  /** P-022: the registry default. ASSUMED — no `width: null` column of this type is measured. */
  defaultWidth: 140,
  align: 'left',
  groupable: true,
  /** P-263: the value is item-independent, so it may be written to a whole selection. */
  massEditable: true,

  Cell: ({ value, settings }) => {
    if (value === null) return <span className="ez-cell__empty" aria-hidden="true" />;
    const flag = settings.show_flag === false ? '' : flagOf(value.countryShortName);
    return (
      <span className="ez-cell__text" data-align="left" title={value.phone}>
        {flag === '' ? null : (
          <span className="ez-phone__flag" aria-hidden="true">
            {flag}
          </span>
        )}
        {value.phone}
      </span>
    );
  },

  Editor: ({ value, initialInput, autoFocus, onCommit, onCancel, onNavigate }) => (
    <InlineTextEditor
      initial={value?.phone ?? ''}
      initialInput={initialInput}
      autoFocus={autoFocus}
      ariaLabel="Phone"
      inputMode="decimal"
      // monday refuses a non-numeric keystroke as it is typed, like `numbers`.
      accept={(next) => next === '' || /^\+?[0-9 ]*$/.test(next)}
      onCommit={(next) => {
        const trimmed = next.replace(/\s+/g, '');
        if (trimmed === '') {
          onCommit(null);
          return;
        }
        onCommit({ phone: trimmed, countryShortName: value?.countryShortName ?? null });
      }}
      onCancel={onCancel}
      onNavigate={onNavigate}
    />
  ),

  SettingsDialog: PhoneSettingsDialog,

  fromClipboard(text, settings) {
    return shared.parseValue(text, settings) as PhoneValue | null;
  },
  toClipboard(value, settings) {
    return shared.toText(value, settings) ?? '';
  },
  bucketsOf: sharedBuckets('phone'),
});
